/**
 * `+ New list` — four fields, every one of them pre-filled.
 *
 * The point of the dialog is that you can dismiss it with Enter and still get a
 * usable list: the name is the next free `List n`, the key is the lowest free
 * digit, the colour is the next unused slot, and the first list a user ever
 * makes takes the crown whether or not the box is ticked. Everything here is a
 * decision you are *allowed* to make, not one you have to.
 *
 * That is what makes "Fav 1, Fav 2, Fav 3" a naming default rather than a second
 * data model: change `word` and every list is born `Fav n`, with no other
 * consequence anywhere.
 *
 * ## Why the key can be taken
 *
 * Typing a digit another list holds does not refuse — the server moves it, and
 * says which list lost it. Somebody who typed `3` meant *make this one 3*, and
 * refusing would send them off to free the digit first. The warning under the
 * field is the whole of the feedback that needs.
 *
 * ## Letters are not offered, and that is not an oversight
 *
 * `lib/type-to-search.ts` takes every plain letter typed anywhere in the app to
 * seed the symbol search. A letter bound here would mean that clicking a
 * screener row and typing `A` filed it into a list instead of charting Apple.
 * Digits are excluded from that listener on purpose — its own comment says they
 * are what a shortcut is made of — so the nine digits are what there is.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  COLOUR_TOKENS,
  colourToken,
  nextFreeColour,
  nextFreeKey,
  nextFreeName,
  type TrackList,
} from "../lib/track-lists";

export interface NewListDialogProps {
  lists: readonly TrackList[];
  /** Token → CSS. Injected, so nothing here reads a theme. */
  resolve: (token: string) => string | undefined;
  /** The symbols the list is born with — the rows that prompted it, if any. */
  seedCount?: number;
  /** The house word: `List`, or `Fav`. */
  word?: string;
  onCreate: (spec: {
    name: string;
    hotKey: string | null;
    colour: string;
    isDefault: boolean;
  }) => void;
  onClose: () => void;
  /** A refusal from the server, shown verbatim — "you already have a list called AI". */
  error?: string | null;
  busy?: boolean;
}

export function NewListDialog({
  lists,
  resolve,
  seedCount = 0,
  word = "List",
  onCreate,
  onClose,
  error,
  busy,
}: NewListDialogProps) {
  const defaults = useMemo(
    () => ({
      name: nextFreeName(word),
      key: nextFreeKey(),
      colour: nextFreeColour(),
    }),
    // Computed once per open: recomputing as the user types would overwrite what
    // they typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [name, setName] = useState("");
  const [key, setKey] = useState(defaults.key ?? "");
  const [colour, setColour] = useState(defaults.colour);
  // On for the very first list, because alt-click needs a target from the first
  // add — and off afterwards, since taking the crown is a deliberate act.
  const [isDefault, setIsDefault] = useState(lists.length === 0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [onClose]);

  const holder = key ? lists.find((list) => list.hotKey === key) : undefined;
  const finalName = name.trim() || defaults.name;

  const submit = () => {
    if (busy) return;
    onCreate({
      name: finalName,
      hotKey: key || null,
      colour,
      isDefault,
    });
  };

  return (
    <div style={SCRIM}>
      <div ref={ref} role="dialog" aria-label="New list" style={PANEL}>
        <h4 style={TITLE}>New list</h4>

        <label style={ROW}>
          <span style={LABEL}>Name</span>
          <input
            autoFocus
            value={name}
            // Placeholder rather than a value, so pressing Create without typing
            // is a decision and not an accident — the grey text says what you
            // will get.
            placeholder={defaults.name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            style={INPUT}
            maxLength={64}
          />
        </label>

        <label style={ROW}>
          <span style={LABEL}>Key</span>
          <input
            value={key}
            onChange={(e) => {
              const next = e.target.value.replace(/[^1-9]/g, "").slice(-1);
              setKey(next);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            style={{ ...INPUT, width: 46, flex: "none", fontFamily: MONO }}
            inputMode="numeric"
            aria-label="Hot key"
          />
          <span style={HINT}>
            {holder
              ? `taken by ${holder.name} — it will move here`
              : key
                ? "press it with rows selected"
                : defaults.key === null
                  ? "all nine are taken — use the menu"
                  : "optional"}
          </span>
        </label>

        <div style={ROW}>
          <span style={LABEL}>Colour</span>
          <div style={{ display: "flex", gap: 5 }}>
            {Object.keys(COLOUR_TOKENS).map((slot) => (
              <button
                key={slot}
                aria-label={slot}
                title={slot}
                onClick={() => setColour(slot)}
                style={{
                  ...SWATCH,
                  background: resolve(colourToken(slot)) ?? "var(--qf-accent)",
                  borderColor: slot === colour ? "var(--qf-text)" : "transparent",
                }}
              />
            ))}
          </div>
        </div>

        <label style={{ ...ROW, marginTop: 12 }}>
          <span style={LABEL} />
          <span style={{ fontSize: 11.5, display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Make this my default list
          </span>
        </label>

        {seedCount > 0 ? (
          <p style={NOTE}>
            {seedCount === 1 ? "1 symbol" : `${seedCount} symbols`} will go in it.
          </p>
        ) : null}

        {error ? <p style={ERROR}>{error}</p> : null}

        <div style={FOOT}>
          <button style={BTN} onClick={onClose}>
            Cancel
          </button>
          <button style={{ ...BTN, ...PRIMARY }} onClick={submit} disabled={busy}>
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

const MONO = "ui-monospace, Menlo, Consolas, monospace";

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: 330,
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 13px", fontSize: 13, fontWeight: 600 };

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  margin: "0 0 9px",
};

const LABEL: React.CSSProperties = {
  width: 52,
  flex: "none",
  color: "var(--qf-text-muted)",
  fontSize: 11,
};

const INPUT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  padding: "4px 7px",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 11.5,
};

const HINT: React.CSSProperties = {
  color: "var(--qf-text-muted)",
  fontSize: 10.5,
  flex: 1,
  minWidth: 0,
};

const SWATCH: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 3,
  border: "1px solid transparent",
  padding: 0,
  cursor: "pointer",
};

const NOTE: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 11,
  color: "var(--qf-text-muted)",
};

const ERROR: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 11,
  color: "var(--qf-negative)",
};

const FOOT: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 7,
  marginTop: 14,
};

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
